// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Profile-photo preparation (2026-08 profile batch, ADR-024): center-crops
 * to a square and downscales to ≤512px, re-encoded JPEG q0.8 (PNG alpha
 * flattened onto white). Same canvas/bitmap approach as
 * components/layout/amyImage.ts — kept separate because the avatar pipeline
 * produces a Blob for a presigned S3 PUT (no base64 leg) and enforces a
 * square aspect the chat attachments don't want.
 *
 * The server never trusts this: the presigned PUT is capped by TTL and
 * content type, and only a confirmed, HeadObject-verified key is persisted.
 */

export const AVATAR_EDGE_PX = 512
export const AVATAR_JPEG_QUALITY = 0.8
/** Anything above this after compression is a decoding bug, not a real avatar. */
export const AVATAR_MAX_BLOB_BYTES = 2 * 1024 * 1024

export interface ProcessedAvatar {
  blob: Blob
  /** Object URL of the compressed blob, for immediate preview. */
  previewUrl: string
}

export interface AvatarProcessFailure {
  error: "tooLarge" | "readError"
}

async function decodeToBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file)
  }
  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    image.src = url
    await image.decode()
    return image
  } finally {
    URL.revokeObjectURL(url)
  }
}

export async function processAvatarFile(
  file: File
): Promise<ProcessedAvatar | AvatarProcessFailure> {
  let bitmap: ImageBitmap | HTMLImageElement
  try {
    bitmap = await decodeToBitmap(file)
  } catch {
    return { error: "readError" }
  }

  const sourceWidth = "naturalWidth" in bitmap ? bitmap.naturalWidth : bitmap.width
  const sourceHeight = "naturalHeight" in bitmap ? bitmap.naturalHeight : bitmap.height
  if (!sourceWidth || !sourceHeight) return { error: "readError" }

  // Center-crop to square, then scale to at most AVATAR_EDGE_PX.
  const cropEdge = Math.min(sourceWidth, sourceHeight)
  const sx = Math.floor((sourceWidth - cropEdge) / 2)
  const sy = Math.floor((sourceHeight - cropEdge) / 2)
  const targetEdge = Math.min(AVATAR_EDGE_PX, cropEdge)

  const canvas = document.createElement("canvas")
  canvas.width = targetEdge
  canvas.height = targetEdge
  const ctx = canvas.getContext("2d")
  if (!ctx) return { error: "readError" }
  ctx.fillStyle = "#ffffff"
  ctx.fillRect(0, 0, targetEdge, targetEdge)
  ctx.drawImage(bitmap, sx, sy, cropEdge, cropEdge, 0, 0, targetEdge, targetEdge)
  if ("close" in bitmap) bitmap.close()

  const blob = await new Promise<Blob | null>(resolve =>
    canvas.toBlob(resolve, "image/jpeg", AVATAR_JPEG_QUALITY)
  )
  if (!blob) return { error: "readError" }
  if (blob.size > AVATAR_MAX_BLOB_BYTES) return { error: "tooLarge" }

  return { blob, previewUrl: URL.createObjectURL(blob) }
}

/** Initials for the avatar fallback: name parts, else the email prefix's first letters. */
export function avatarInitials(
  firstName: string | null | undefined,
  familyName: string | null | undefined,
  email: string | null | undefined
): string {
  const first = firstName?.trim()
  const family = familyName?.trim()
  if (first || family) {
    return `${first?.[0] ?? ""}${family?.[0] ?? ""}`.toUpperCase() || "?"
  }
  const prefix = (email ?? "").split("@")[0]
  return prefix.slice(0, 2).toUpperCase() || "?"
}

/** Display name with the honest fallback: email prefix when no name is on file. */
export function displayName(
  firstName: string | null | undefined,
  familyName: string | null | undefined,
  email: string | null | undefined
): string {
  const parts = [firstName?.trim(), familyName?.trim()].filter(Boolean)
  if (parts.length > 0) return parts.join(" ")
  return (email ?? "").split("@")[0] || ""
}
